import classNames from 'classnames'
import { Phone as PhoneIcon } from 'iconoir-react'
import { capitalize, compact } from 'lodash'
import moment from 'moment'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import Calendar from 'react-calendar'
import Combo from '../Combo'
import Spinner from '../Spinner'
import { MobileOverlay } from './MobileOverlay'
import { SchedulingWizardContext } from './SchedulingWizard'
import { TimeSlot } from './TimeSlot'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { LocationJson } from 'app/models/Location'
import { SchedulingConfigurationJson } from 'app/models/SchedulingConfiguration'
import { useHandleSearchParams } from 'app/utils'
import { OptionType } from 'types/OptionType'

const ANY_PROVIDER: OptionType = { id: ``, name: `Any`, value: { displayName: `Any` } }

export function AppointmentConfig({
  location,
  schedulingConfiguration,
  startDate,
}: {
  location: LocationJson
  schedulingConfiguration?: SchedulingConfigurationJson
  startDate: string | undefined
}): ReactElement {
  const {
    appointmentTypes,
    apptType,
    back,
    patientType,
    provider,
    providers,
    selectedDaySlotIndex,
    selectedTimeSlot,
    daySlots,
    setApptType,
    setFlowMode,
    setProvider,
    setSelectedTimeSlot,
    setSelectedAvailability,
    setSelectedDaySlotIndex,
  } = useContext(SchedulingWizardContext)

  const handleSearchParams = useHandleSearchParams()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [selectedProvider, setSelectedProvider] = useState<OptionType>(ANY_PROVIDER)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const appointmentTypeOptions = appointmentTypes
    .filter((type) => !type.patientType || type.patientType === patientType)
    ?.map((apptType) => ({
      id: apptType.id!,
      value: apptType,
      name: apptType.alias ?? apptType.displayName!,
    }))
  const selectedAppointmentType = useMemo(
    () => appointmentTypeOptions.find((option) => option.id === apptType?.id),
    [apptType],
  )

  const providerOptions = [
    ANY_PROVIDER,
    ...providers.map((provider) => ({
      id: provider.id!,
      value: provider,
      name: capitalize(provider.displayName!),
    })),
  ]

  useEffect(() => {
    setFlowMode(`regular`)
  }, [])

  // Reset isLoading when startDate changes (new data has loaded)
  useEffect(() => {
    if (isLoading && startDate) {
      setIsLoading(false)
    }
  }, [startDate])

  if (!startDate) {
    return <div></div>
  }

  return (
    <div
      className={`grid h-full grid-cols-1 gap-y-6 overflow-hidden animate-duration-[400ms] md:grid-cols-[60%_40%] md:grid-rows-[auto_auto_1fr] ${
        back ? `animate-fade-right` : `animate-fade-left`
      }`}
    >
      {isLoading && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .schedule-calendar .react-calendar__viewContainer {
                display: none !important;
              }
            `,
          }}
        />
      )}
      <div className="mr-0 hidden flex-col gap-y-1.5 md:mr-6 md:flex">
        <span className="text-sm font-medium text-neutral-700">Appointment type</span>
        <Combo
          options={appointmentTypeOptions}
          value={selectedAppointmentType}
          onChange={(option) => {
            setApptType(option.value)
            setSelectedTimeSlot(undefined)
            setSelectedAvailability?.(undefined)
            if (provider) {
              setProvider(undefined)
              setSelectedProvider(ANY_PROVIDER)
            }
          }}
          inputClassName="!py-3.5 !px-4 text-body-sm text-trn-900"
        />
      </div>
      <div className="hidden flex-col gap-y-1.5 md:flex">
        <span className="text-sm font-medium text-neutral-700">Provider</span>
        <Combo
          options={providerOptions}
          value={{
            id: selectedProvider.id,
            name: selectedProvider.value.displayName,
            value: selectedProvider,
          }}
          onChange={(option) => {
            if (!option?.id || option.id !== provider?.id) {
              setSelectedAvailability?.(undefined)
              setIsOpen(false)
            }
            setProvider(option.id === ANY_PROVIDER.id ? undefined : option.value)
            setSelectedTimeSlot(undefined)
            setSelectedProvider(option)
          }}
          inputClassName="!py-3.5 !px-4 text-body-sm text-trn-900"
        />
      </div>
      <div className="mr-0 mt-0 flex flex-col md:mr-6">
        <span className="hidden text-ui-sm text-neutral-900 md:flex">Select a date and time</span>
        <div className="flex flex-col gap-4">
          <Calendar
            goToRangeStartOnSelect={false}
            defaultView="month"
            maxDetail="month"
            minDetail="month"
            minDate={new Date()}
            next2Label={null}
            prev2Label={null}
            className="schedule-calendar flex flex-col gap-4"
            formatShortWeekday={(locale, date) => moment(date).format(`dd`).charAt(0)}
            tileClassName={({ date }) =>
              classNames(
                `aspect-square rounded-lg text-center text-sm font-medium leading-none hover:bg-blue-600 hover:text-white disabled:rounded-lg disabled:bg-white disabled:text-trn-400`,
                {
                  [`underline underline-offset-4`]: moment(date).startOf(`d`).isSame(moment().startOf(`d`)),
                  'md:!bg-[#0051E9] md:!text-trn-50 !bg-trn-100 !text-trn-900':
                    selectedDaySlotIndex >= 0
                      ? moment(date)
                          .startOf(`d`)
                          .isSame(moment(startDate).add(selectedDaySlotIndex, `days`).startOf(`d`))
                      : false,
                  'bg-trn-100 text-trn-900': !moment(date)
                    .startOf(`d`)
                    .isSame(moment(startDate).add(selectedDaySlotIndex, `days`).startOf(`d`)),
                },
              )
            }
            onChange={(date: Date) => {
              const selectedDaySlotIndex = moment(date).startOf(`day`).diff(moment(startDate).startOf(`day`), `days`)
              setSelectedDaySlotIndex(selectedDaySlotIndex)
              const timeSlot = daySlots?.[selectedDaySlotIndex]?.timeSlots?.find(
                (timeSlot) => timeSlot.time === selectedTimeSlot?.time && compact(timeSlot?.availabilities).length,
              )
              setSelectedTimeSlot(timeSlot)
              setIsOpen(true)
            }}
            selectRange={false}
            calendarType={`US`}
            onActiveStartDateChange={({ activeStartDate }) => {
              handleSearchParams({ day: moment(activeStartDate).format(`YYYY-MM-DD`) })
              setIsLoading(true)
              // Reset selection when navigating months to find earliest available date
              setSelectedDaySlotIndex(-1)
            }}
            value={selectedDaySlotIndex >= 0 ? moment(startDate).add(selectedDaySlotIndex, `days`).toDate() : undefined}
            tileDisabled={({ date }) => {
              // Disable dates in the past
              if (moment(date).startOf(`day`).isBefore(moment().startOf(`day`))) {
                return true
              }
              // Disable dates with no available time slots
              return !daySlots?.[moment(date).startOf(`day`).diff(moment(startDate).startOf(`day`), `days`)]?.timeSlots
                ?.length
            }}
          />
          {!isLoading && schedulingConfiguration?.trackingPhoneNumber && (
            <div className="mt-6 flex flex-col items-center gap-y-3 px-6 py-3">
              <span className="shrink-0 text-heading-sm text-black md:text-ui-md">Can't find your preferred time?</span>
              <a
                href={`tel:${schedulingConfiguration.trackingPhoneNumber}`}
                className="flex w-full items-center justify-center gap-x-2 rounded-lg border border-[#0067FD] px-6 py-3 shadow-elevation-1 hover:border-[#1D4ED8] hover:bg-blue-500/[0.04] md:w-fit md:grow-0"
              >
                <PhoneIcon height={16} width={16} className="text-[#0067FD]" />
                <span className="text-sm font-medium leading-tight text-[#0067FD]">
                  {formatPhoneNumber(schedulingConfiguration.trackingPhoneNumber)}
                </span>
              </a>
            </div>
          )}
        </div>
      </div>

      <div className={`relative hidden flex-col gap-y-6 overflow-hidden md:flex ${isLoading ? `invisible` : ``}`}>
        <TimeSlot location={location} startDate={startDate} />
      </div>

      {isLoading && (
        <div className="flex flex-col items-center justify-start gap-2 py-4 md:col-span-2 md:py-8">
          <Spinner size={16} />
          <span className="text-sm text-neutral-600">Loading availability...</span>
        </div>
      )}

      {!isLoading && (
        <MobileOverlay closeLabel="Select a time" open={isOpen} onClose={() => setIsOpen(false)}>
          <TimeSlot location={location} startDate={startDate} />
        </MobileOverlay>
      )}
    </div>
  )
}
